/**
 * Turning a picked file into a brand logo the platform will accept.
 *
 * The stored value is a data URL, because the signing page renders it through
 * an `<img>` — an SVG in an image tag runs in the SVG spec's secure static
 * mode, so it cannot execute script, reach the parent document, or fetch
 * external resources. Inlining the same markup would allow all three.
 *
 * Two things this has to get right beyond "read the file":
 *
 *  1. **Trust the bytes, not the name.** A file called `logo.png` is whatever
 *     its contents say it is, so the type is sniffed rather than taken from
 *     the extension or the browser's reported MIME type.
 *  2. **Fit the cap without failing the user.** The stored column is bounded
 *     at 64KB, which after base64's ~33% overhead leaves roughly 48KB of
 *     image. A normal logo export blows past that, so a PNG is downscaled
 *     until it fits instead of being rejected. SVGs are left alone — they are
 *     already small, and rasterising one would throw away the reason to use
 *     it.
 */

import { DS_BRAND_LOGO_MAX_BYTES } from '@/lib/api';

/** Longest edge a downscaled logo is reduced to, in CSS pixels. */
const MAX_LOGO_EDGE = 256;

/** PNG file signature. Sniffed after decode, so a renamed file cannot pass. */
const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export class BrandLogoError extends Error {}

function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new BrandLogoError('Could not read that file'));
    reader.readAsArrayBuffer(file);
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function looksLikePng(bytes: Uint8Array): boolean {
  return PNG_MAGIC.every((b, i) => bytes[i] === b);
}

/**
 * An SVG is text, so "is this an SVG" is a parse question. Rejecting anything
 * that does not parse as XML with an `<svg>` root also rejects the HTML
 * documents people occasionally pick by mistake.
 */
function looksLikeSvg(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith('<')) return false;
  try {
    const doc = new DOMParser().parseFromString(trimmed, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return false;
    return doc.documentElement?.tagName?.toLowerCase() === 'svg';
  } catch {
    return false;
  }
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new BrandLogoError('That PNG could not be decoded'));
    img.src = dataUrl;
  });
}

/**
 * Shrink a PNG until its data URL fits the cap. Each pass halves the longest
 * edge, so a very large source converges in a few iterations rather than
 * looping on diminishing returns.
 */
async function shrinkPngToFit(dataUrl: string): Promise<string> {
  if (dataUrl.length <= DS_BRAND_LOGO_MAX_BYTES) return dataUrl;

  const img = await loadImage(dataUrl);
  let edge = Math.min(MAX_LOGO_EDGE, Math.max(img.width, img.height));
  let current = dataUrl;

  for (let attempt = 0; attempt < 5; attempt++) {
    const scale = edge / Math.max(img.width, img.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new BrandLogoError('This browser could not resize the image');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    current = canvas.toDataURL('image/png');
    if (current.length <= DS_BRAND_LOGO_MAX_BYTES) return current;
    edge = Math.max(16, Math.round(edge / 2));
  }

  throw new BrandLogoError(
    'That image is too detailed to fit the logo size limit. Try a simpler mark, or upload an SVG.',
  );
}

/**
 * Convert a picked file to a stored logo value, or throw a message that says
 * what to do about it.
 */
export async function fileToBrandLogoDataUrl(file: File): Promise<string> {
  const buffer = await readAsArrayBuffer(file);
  const bytes = new Uint8Array(buffer);

  if (looksLikePng(bytes)) {
    const dataUrl = `data:image/png;base64,${bytesToBase64(bytes)}`;
    return await shrinkPngToFit(dataUrl);
  }

  const text = new TextDecoder().decode(bytes);
  if (looksLikeSvg(text)) {
    const dataUrl = `data:image/svg+xml;base64,${bytesToBase64(bytes)}`;
    if (dataUrl.length > DS_BRAND_LOGO_MAX_BYTES) {
      throw new BrandLogoError(
        'That SVG is larger than the 64KB logo limit. Exporting it without embedded images usually fixes it.',
      );
    }
    return dataUrl;
  }

  throw new BrandLogoError('A logo must be a PNG or an SVG file');
}
